import { useParams, Navigate, Link } from 'react-router-dom';
import { SeoPage, CtaInline } from './SeoLayout';
import { BUSINESS, getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { SERVICES } from './LeistungenOverview';

export default function LeistungDetail() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  if (!service) return <Navigate to="/leistungen" replace />;

  const path = `/leistungen/${service.slug}`;
  const title = `${service.title} in Dormagen und Umgebung | FarbWelt`;
  const desc = `${service.short}. Transparenter Festpreis, kostenlose Besichtigung, Ausführung in Dormagen, Neuss, Köln, Düsseldorf und Umgebung.`;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    serviceType: service.title,
    description: service.intro,
    provider: {
      '@type': 'HomeAndConstructionBusiness',
      name: BUSINESS.legalName,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: BUSINESS.street,
        postalCode: BUSINESS.postalCode,
        addressLocality: BUSINESS.city,
        addressRegion: BUSINESS.region,
        addressCountry: BUSINESS.country
      }
    },
    areaServed: BUSINESS.serviceAreas.map(a => ({ '@type': 'City', name: a })),
    url: `${BUSINESS.domain}${path}`,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: `${BUSINESS.domain}${path}`
    }
  };

  const otherServices = SERVICES.filter(s => s.slug !== service.slug).slice(0, 3);

  return (
    <SeoPage
      title={title}
      description={desc}
      canonicalPath={path}
      kicker={service.metaTitle.split(' in ')[0]}
      heroTitle={`${service.title} — sauber und pünktlich ausgeführt`}
      heroSubtitle={service.short}
      jsonLd={[
        getLocalBusinessSchema(),
        serviceSchema,
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Leistungen', item: '/leistungen' },
          { name: service.title, item: path }
        ])
      ]}
    >
      <p className="lead">{service.intro}</p>

      <h2>Was zu dieser Leistung gehört</h2>
      <ul className="not-prose grid sm:grid-cols-2 gap-3 my-6">
        {service.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 p-3 bg-[#faf7f5] rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
            <span className="text-gray-800">{b}</span>
          </li>
        ))}
      </ul>

      <h2>Wann sich das für Sie lohnt</h2>
      <p>{service.goodFor}</p>

      <CtaInline text={`${service.title} anfragen — kostenlos und unverbindlich`} />

      <h2>So gehen wir vor</h2>
      <ol>
        <li><strong>Anfrage</strong> per Telefon <a href={`tel:${BUSINESS.phone}`}>{BUSINESS.phoneDisplay}</a> oder Online-Termin.</li>
        <li><strong>Kostenlose Besichtigung</strong> vor Ort — meist innerhalb weniger Tage.</li>
        <li><strong>Schriftliches Festpreis-Angebot</strong> mit klarem Zeitrahmen und Materialauswahl.</li>
        <li><strong>Ausführung</strong> zum vereinbarten Termin durch unser Team, sauber und pünktlich.</li>
        <li><strong>Übergabe und Rechnung</strong> — Arbeitsleistung separat ausgewiesen für Ihre Steuererklärung (§&nbsp;35a EStG).</li>
      </ol>

      <h2>Einsatzgebiet für {service.title.toLowerCase()}</h2>
      <p>
        Hauptsitz Dormagen. Wir arbeiten regelmäßig in <strong>Dormagen und allen Stadtteilen</strong> (Zons,
        Nievenheim, Delhoven, Hackenbroich, Stürzelberg, Straberg u.&nbsp;a.) sowie in{' '}
        <strong>Neuss, Köln, Düsseldorf, Leverkusen, Monheim am Rhein, Grevenbroich und Meerbusch</strong>.
        Details zu Fahrt- oder Anfahrtsregelung besprechen wir transparent im Angebot.{' '}
        <Link to="/einsatzgebiet">Alle Einsatzgebiete ansehen →</Link>
      </p>

      <h2>Weitere passende Leistungen</h2>
      <div className="not-prose grid sm:grid-cols-3 gap-4 my-4">
        {otherServices.map(s => (
          <Link
            key={s.slug}
            to={`/leistungen/${s.slug}`}
            className="group p-4 bg-white border border-gray-200 rounded-lg hover:border-[#9B1B30] hover:shadow-md transition-all block"
          >
            <div className="flex items-center gap-3 mb-1">
              <s.icon className="w-5 h-5 text-[#9B1B30]" />
              <h3 className="font-bold text-[#1A1A1A] mt-0 group-hover:text-[#9B1B30]">{s.title}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-0">{s.short}</p>
          </Link>
        ))}
      </div>

      <p className="text-sm text-gray-500 mt-8">
        Mehr über den Betrieb: <a href={BUSINESS.externalSite} target="_blank" rel="noopener">farbwelt-service.de</a>{' '}
        · <Link to="/faq">Häufige Fragen</Link> · <Link to="/so-arbeiten-wir">Unser Ablauf</Link>
      </p>
    </SeoPage>
  );
}
